import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select,
  TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import { GuestGateMessage } from './GuestGateMessage';
import {
  DEFAULT_FAMILY_TREE_SETTINGS, deleteFamilyMember, saveFamilyMember,
  loadFamilyMemberPhoto, saveFamilyTreeSettings, subscribeFamilyTree,
} from '../../firebase/familyTreeService';

const EMPTY_MEMBER = {
  firstName: '', surname: '', birthDate: '', deathDate: '', countryOfBirth: '',
  profession: '', facts: '', parentIds: [], spouseIds: [], photo: null,
};

const FONT_SIZES = { small: 12, medium: 14, large: 17 };
const TREE_COLORS = {
  classic: { accent: '#D6A75C', border: 'rgba(214,167,92,0.28)', background: 'rgba(46,32,18,0.72)' },
  modern: { accent: '#4CC9F0', border: 'rgba(76,201,240,0.26)', background: 'rgba(10,25,40,0.8)' },
  compact: { accent: '#06D6A0', border: 'rgba(6,214,160,0.24)', background: 'rgba(8,30,27,0.78)' },
};

function useFamilyMemberPhoto(userId, photo) {
  const [url, setUrl] = useState('');
  const [failed, setFailed] = useState(false);
  const photoPath = photo?.path;
  const contentType = photo?.contentType;

  useEffect(() => {
    if (!photoPath) {
      setUrl('');
      setFailed(false);
      return undefined;
    }
    let active = true;
    let objectUrl = '';
    setUrl('');
    setFailed(false);
    loadFamilyMemberPhoto(userId, { path: photoPath, contentType }).then((loadedUrl) => {
      objectUrl = loadedUrl;
      if (active) setUrl(loadedUrl);
      else URL.revokeObjectURL(loadedUrl);
    }).catch(() => {
      if (active) setFailed(true);
    });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [userId, photoPath, contentType]);

  return { url, failed };
}

function getGenerationRows(members) {
  const byId = new Map(members.map((member) => [member.id, member]));
  const generations = new Map();
  const visiting = new Set();
  const generationFor = (member) => {
    if (generations.has(member.id)) return generations.get(member.id);
    if (visiting.has(member.id)) return 0;
    visiting.add(member.id);
    const parentGenerations = (member.parentIds || [])
      .map((parentId) => byId.get(parentId))
      .filter(Boolean)
      .map(generationFor);
    visiting.delete(member.id);
    const generation = parentGenerations.length ? Math.max(...parentGenerations) + 1 : 0;
    generations.set(member.id, generation);
    return generation;
  };

  members.forEach(generationFor);
  const rows = new Map();
  members.forEach((member) => {
    const generation = generations.get(member.id);
    if (!rows.has(generation)) rows.set(generation, []);
    rows.get(generation).push(member);
  });
  return [...rows.entries()]
    .sort(([a], [b]) => a - b)
    .map(([generation, rowMembers]) => ({
      generation,
      members: rowMembers.sort((a, b) => a.firstName.localeCompare(b.firstName)),
    }));
}

function formatDate(value, dateFormat) {
  if (!value) return '';
  const [year, month, day] = value.split('-');
  if (dateFormat === 'MM/DD/YYYY') return `${month}/${day}/${year}`;
  if (dateFormat === 'YYYY-MM-DD') return value;
  return `${day}/${month}/${year}`;
}

function memberLabel(member) {
  return `${member.firstName} ${member.surname}`.trim();
}

function MemberDialog({ open, member, members, defaultSurname, userId, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_MEMBER);
  const [photoFile, setPhotoFile] = useState(null);
  const [originalPhoto, setOriginalPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const savedPhoto = useFamilyMemberPhoto(userId, form.photo);

  useEffect(() => {
    if (!open) return undefined;
    setForm(member ? { ...EMPTY_MEMBER, ...member } : { ...EMPTY_MEMBER, surname: defaultSurname });
    setPhotoFile(null);
    setOriginalPhoto(member?.photo || null);
    setPhotoPreview('');
    setError('');
    setSaving(false);
    return undefined;
  }, [open, member, defaultSurname]);

  useEffect(() => {
    if (!photoFile) return undefined;
    const url = URL.createObjectURL(photoFile);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photoFile]);

  const descendants = useMemo(() => {
    if (!member) return new Set();
    const found = new Set([member.id]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const candidate of members) {
        if (!found.has(candidate.id) && (candidate.parentIds || []).some((id) => found.has(id))) {
          found.add(candidate.id);
          changed = true;
        }
      }
    }
    return found;
  }, [member, members]);

  const setField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleSave = async () => {
    if (!form.firstName.trim()) {
      setError('Enter a first name.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(form, photoFile, originalPhoto);
      onClose();
    } catch (saveError) {
      setError(saveError.message || 'Could not save this family member.');
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { bgcolor: '#0d1520', color: '#e6edf3', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 900 }}>{member ? 'Edit family member' : 'Add family member'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: '12px !important' }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <TextField label="First name" value={form.firstName} onChange={setField('firstName')} required />
          <TextField label="Surname" value={form.surname} onChange={setField('surname')} />
          <TextField label="Date of birth" type="date" value={form.birthDate} onChange={setField('birthDate')}
            InputLabelProps={{ shrink: true }} />
          <TextField label="Date of death" type="date" value={form.deathDate} onChange={setField('deathDate')}
            InputLabelProps={{ shrink: true }} />
          <TextField label="Country of birth" value={form.countryOfBirth} onChange={setField('countryOfBirth')} />
          <TextField label="Profession" value={form.profession} onChange={setField('profession')} />
        </Box>
        <FormControl>
          <InputLabel id="family-parents-label">Parents</InputLabel>
          <Select labelId="family-parents-label" multiple label="Parents" value={form.parentIds || []}
            onChange={setField('parentIds')} renderValue={(ids) => ids.map((id) => memberLabel(members.find((item) => item.id === id) || {})).join(', ')}>
            {members.filter((item) => !descendants.has(item.id)).map((item) => (
              <MenuItem key={item.id} value={item.id}>
                <Checkbox checked={(form.parentIds || []).includes(item.id)} />
                {memberLabel(item)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <InputLabel id="family-partners-label">Spouse or partner</InputLabel>
          <Select labelId="family-partners-label" multiple label="Spouse or partner" value={form.spouseIds || []}
            onChange={setField('spouseIds')} renderValue={(ids) => ids.map((id) => memberLabel(members.find((item) => item.id === id) || {})).join(', ')}>
            {members.filter((item) => item.id !== member?.id).map((item) => (
              <MenuItem key={item.id} value={item.id}>
                <Checkbox checked={(form.spouseIds || []).includes(item.id)} />
                {memberLabel(item)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField label="Interesting facts or life story" value={form.facts} onChange={setField('facts')}
          multiline minRows={3} inputProps={{ maxLength: 2000 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button component="label" variant="outlined" startIcon={<PhotoCameraIcon />}>
            Add photo
            <input hidden type="file" accept="image/*" onChange={(event) => {
              setPhotoFile(event.target.files?.[0] || null);
              event.target.value = '';
            }} />
          </Button>
          <Typography sx={{ color: '#8b949e', fontSize: '0.75rem' }}>
            {photoFile?.name || (form.photo ? 'Current photo saved' : 'Optional, up to 5 MB')}
          </Typography>
          {(photoPreview || savedPhoto.url) && (
            <Avatar src={photoPreview || savedPhoto.url} alt="" sx={{ width: 42, height: 42, ml: 'auto' }} />
          )}
          {form.photo && (
            <Button size="small" onClick={() => {
              setForm((current) => ({ ...current, photo: null }));
              setPhotoFile(null);
            }} sx={{ color: '#f87171' }}>
              Remove
            </Button>
          )}
          {savedPhoto.failed && (
            <Typography role="alert" sx={{ color: '#fca5a5', fontSize: '0.7rem' }}>Saved photo could not be loaded.</Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: '#9aa4af' }}>Cancel</Button>
        <Button onClick={handleSave} disabled={saving} variant="contained" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{ bgcolor: '#06D6A0', color: '#08130f', fontWeight: 800, '&:hover': { bgcolor: '#05bd8d' } }}>
          {saving ? 'Saving' : 'Save member'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MemberCard({ userId, member, members, settings, onEdit, onDelete }) {
  const theme = TREE_COLORS[settings.treeStyle] || TREE_COLORS.classic;
  const textSize = FONT_SIZES[settings.fontSize] || FONT_SIZES.medium;
  const photo = useFamilyMemberPhoto(userId, member.photo);
  const parents = (member.parentIds || [])
    .map((id) => members.find((item) => item.id === id))
    .filter(Boolean);
  const partners = members.filter((item) =>
    (member.spouseIds || []).includes(item.id) || (item.spouseIds || []).includes(member.id));
  const dates = [formatDate(member.birthDate, settings.dateFormat), formatDate(member.deathDate, settings.dateFormat)]
    .filter(Boolean).join(' – ');

  return (
    <Box sx={{
      width: { xs: 250, sm: 280 }, flexShrink: 0, p: 1.5, borderRadius: 3,
      background: theme.background, border: `1px solid ${theme.border}`,
      boxShadow: settings.treeStyle === 'classic' ? '0 6px 22px rgba(0,0,0,0.22)' : 'none',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
        <Avatar src={photo.url || undefined} alt={memberLabel(member)}
          sx={{ width: 48, height: 48, bgcolor: `${theme.accent}30`, color: theme.accent, fontWeight: 900 }}>
          {member.firstName?.slice(0, 1)?.toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ color: '#f0f6fc', fontSize: textSize, fontWeight: 900, overflowWrap: 'anywhere' }}>
            {memberLabel(member)}
          </Typography>
          {dates && <Typography sx={{ color: '#9aa4af', fontSize: textSize - 2 }}>{dates}</Typography>}
        </Box>
        <IconButton aria-label={`Edit ${memberLabel(member)}`} onClick={() => onEdit(member)} size="small" sx={{ color: '#9aa4af' }}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton aria-label={`Delete ${memberLabel(member)}`} onClick={() => onDelete(member)} size="small" sx={{ color: '#f87171' }}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
      {photo.failed && (
        <Typography role="alert" sx={{ mt: 0.6, color: '#fca5a5', fontSize: '0.68rem' }}>
          Could not load this member’s photo.
        </Typography>
      )}
      {(member.profession || member.countryOfBirth) && (
        <Typography sx={{ mt: 1, color: '#c9d1d9', fontSize: textSize - 1 }}>
          {[member.profession, member.countryOfBirth].filter(Boolean).join(' · ')}
        </Typography>
      )}
      {parents.length > 0 && (
        <Typography sx={{ mt: 0.8, color: '#9aa4af', fontSize: textSize - 2 }}>
          Parents: {parents.map(memberLabel).join(', ')}
        </Typography>
      )}
      {partners.length > 0 && (
        <Typography sx={{ mt: 0.5, color: '#9aa4af', fontSize: textSize - 2 }}>
          Spouse/partner: {partners.map(memberLabel).join(', ')}
        </Typography>
      )}
      {member.facts && (
        <Typography sx={{ mt: 1, color: '#bac4ce', fontSize: textSize - 1, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {member.facts}
        </Typography>
      )}
    </Box>
  );
}

export function FamilyTreeSection({ userId, isAnonymous }) {
  const [settings, setSettings] = useState(DEFAULT_FAMILY_TREE_SETTINGS);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [dialogMember, setDialogMember] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    if (!userId || isAnonymous) return undefined;
    setLoading(true);
    setError('');
    return subscribeFamilyTree(userId, {
      onSettings: (nextSettings) => {
        setSettings(nextSettings);
        setLoading(false);
      },
      onMembers: (nextMembers) => {
        setMembers(nextMembers);
        setLoading(false);
      },
      onError: (loadError) => {
        setError(`Could not load your family tree: ${loadError.message || 'Firebase request failed.'}`);
        setLoading(false);
      },
    });
  }, [userId, isAnonymous]);

  const updateSettings = (key) => (event) => {
    setSettings((current) => ({ ...current, [key]: event.target.value }));
    setNotice('');
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setError('');
    setNotice('');
    try {
      await saveFamilyTreeSettings(userId, settings);
      setNotice('Tree settings saved.');
    } catch (saveError) {
      setError(`Could not save tree settings: ${saveError.message || 'Firebase request failed.'}`);
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSaveMember = async (form, photoFile, originalPhoto) => {
    setError('');
    setNotice('');
    const result = await saveFamilyMember(userId, form, photoFile, originalPhoto);
    setNotice(result.photoCleanupFailed
      ? 'Member saved, but an older photo could not be removed from storage.'
      : 'Family member saved.');
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Delete ${memberLabel(member)} from your family tree?`)) return;
    setError('');
    setNotice('');
    try {
      const result = await deleteFamilyMember(userId, member);
      setNotice(result.photoCleanupFailed
        ? 'Member deleted, but their photo could not be removed from storage.'
        : 'Family member deleted.');
    } catch (deleteError) {
      setError(`Could not delete this family member: ${deleteError.message || 'Firebase request failed.'}`);
    }
  };

  if (isAnonymous) return <GuestGateMessage feature="Family Tree" />;

  const rows = getGenerationRows(members);

  return (
    <Box sx={{ color: '#e6edf3', pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <AccountTreeIcon sx={{ color: '#06D6A0' }} />
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 900 }}>Family Tree</Typography>
      </Box>
      <Typography sx={{ color: '#9aa4af', fontSize: '0.82rem', lineHeight: 1.6, mb: 1.5 }}>
        Add relatives, connect parents and partners, and preserve the details and stories that matter.
      </Typography>
      <Alert severity="info" sx={{ mb: 1.5, bgcolor: 'rgba(76,201,240,0.08)', color: '#c9d1d9', '& .MuiAlert-icon': { color: '#4CC9F0' } }}>
        Your tree and photos are private to your Google account. Only add personal details and photos you have permission to store.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 1.5 }}>{notice}</Alert>}

      <Box sx={{ mb: 2, p: 1.5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <Typography sx={{ fontSize: '0.76rem', fontWeight: 900, color: '#9aa4af', letterSpacing: '0.08em', mb: 1.2 }}>
          TREE DISPLAY
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: '1.2fr 1fr 1fr 1fr' }, gap: 1 }}>
          <TextField label="Family surname" value={settings.surname} onChange={updateSettings('surname')} />
          <FormControl>
            <InputLabel id="tree-style-label">Tree style</InputLabel>
            <Select labelId="tree-style-label" label="Tree style" value={settings.treeStyle} onChange={updateSettings('treeStyle')}>
              <MenuItem value="classic">Classic</MenuItem>
              <MenuItem value="modern">Modern</MenuItem>
              <MenuItem value="compact">Compact</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="tree-date-format-label">Date format</InputLabel>
            <Select labelId="tree-date-format-label" label="Date format" value={settings.dateFormat} onChange={updateSettings('dateFormat')}>
              <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
              <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
              <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="tree-font-size-label">Font size</InputLabel>
            <Select labelId="tree-font-size-label" label="Font size" value={settings.fontSize} onChange={updateSettings('fontSize')}>
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="large">Large</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Button onClick={handleSaveSettings} disabled={savingSettings || loading} startIcon={savingSettings ? <CircularProgress size={16} /> : <SaveIcon />}
          sx={{ mt: 1.2, color: '#06D6A0', fontWeight: 800 }}>
          Save tree settings
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: '#c9d1d9' }}>
          FAMILY MEMBERS {members.length ? `· ${members.length}` : ''}
        </Typography>
        <Button onClick={() => { setDialogMember(null); setDialogOpen(true); }}
          startIcon={<AddIcon />} variant="contained"
          sx={{ bgcolor: '#06D6A0', color: '#08130f', fontWeight: 900, borderRadius: 2, '&:hover': { bgcolor: '#05bd8d' } }}>
          Add relative
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress sx={{ color: '#06D6A0' }} /></Box>
      ) : members.length === 0 ? (
        <Box sx={{ py: 5, px: 2, textAlign: 'center', borderRadius: 3, border: '1px dashed rgba(255,255,255,0.12)' }}>
          <AccountTreeIcon sx={{ fontSize: 38, color: '#485563', mb: 1 }} />
          <Typography sx={{ color: '#c9d1d9', fontWeight: 800 }}>Your tree is ready to grow</Typography>
          <Typography sx={{ color: '#8b949e', fontSize: '0.78rem', mt: 0.5 }}>Add a relative to start building your family tree.</Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          {rows.map((row) => (
            <Box key={row.generation}>
              <Typography sx={{ color: '#8b949e', fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.1em', mb: 0.7 }}>
                {row.generation === 0 ? 'OLDEST GENERATION' : `GENERATION ${row.generation + 1}`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.6 }}>
                {row.members.map((member) => (
                  <MemberCard key={member.id} userId={userId} member={member} members={members} settings={settings}
                    onEdit={(selected) => { setDialogMember(selected); setDialogOpen(true); }}
                    onDelete={handleDeleteMember} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <MemberDialog open={dialogOpen} member={dialogMember} members={members}
        defaultSurname={settings.surname} userId={userId}
        onClose={() => setDialogOpen(false)} onSave={handleSaveMember} />
    </Box>
  );
}
